<script lang="ts" module>
  export interface DesktopOnlyAction {
    /** Button text, e.g. "📆 Days". */
    label: string;
    onSelect: () => void;
  }
</script>

<script lang="ts">
  /**
   * DesktopOnlyNotice - shown on phones in place of a feature that only has a
   * desktop layout (plans/mobile-v2.md §3.6). Offers buttons to the nearest
   * mobile alternative. `page` fills the content area; `banner` is a strip
   * above a `fallback` (e.g. a read-only list).
   */
  import type { Snippet } from 'svelte';

  interface Props {
    /** Subject of the sentence, e.g. "The Gantt view". */
    feature: string;
    variant?: 'page' | 'banner';
    /** Buttons to the mobile alternatives. */
    actions?: DesktopOnlyAction[];
    /** Content rendered below the notice. */
    fallback?: Snippet;
  }

  let { feature, variant = 'page', actions = [], fallback }: Props = $props();
</script>

<section class="don don-{variant}" aria-label="Desktop only">
  <p class="don-message">🖥️ {feature} is designed for larger screens.</p>
  {#if actions.length > 0}
    {#if variant === 'page'}
      <p class="don-hint">Switch to a view made for your phone:</p>
    {/if}
    <div class="don-actions">
      {#each actions as action (action.label)}
        <button type="button" class="don-action" onclick={action.onSelect}>{action.label}</button>
      {/each}
    </div>
  {/if}
</section>
{@render fallback?.()}

<style>
  .don {
    color: #374151;
  }

  .don-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 22rem;
    margin: 0 auto;
    padding: 48px 16px 24px;
    text-align: center;
  }

  .don-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    margin: 8px 12px;
    padding: 10px 12px;
    border: 1px solid #fde68a;
    border-radius: 12px;
    background: #fffbeb;
  }

  .don-message {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.35;
    color: #111827;
  }

  .don-banner .don-message {
    flex: 1 1 12rem;
    font-size: 14px;
    font-weight: 500;
  }

  .don-hint {
    margin: 8px 0 20px;
    font-size: 15px;
    color: #6b7280;
  }

  .don-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .don-banner .don-actions {
    flex-direction: row;
    width: auto;
  }

  .don-action {
    min-height: 44px;
    padding: 0 16px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    color: #111827;
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .don-action:active {
    background: #f3f4f6;
  }
</style>
